/**
 * EnhancedFillBlankQuestion.tsx
 * Enhanced fill-in-the-blank component with template support and interactive blanks
 * Supports the new fillBlank structure with multiple blanks, choices, and validation
 */

import React, { useCallback, useRef, useEffect } from 'react';
import { cn } from '@vsat/shared/lib/utils';
import { Card } from '@vsat/shared/components';
import { Label } from '@vsat/shared/components';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@vsat/shared/components';
import { RadioGroup, RadioGroupItem } from '@vsat/shared/components';
import { Checkbox } from '@vsat/shared/components';
// Simple FillBlankItem interface for this component
interface FillBlankItem {
  id: string;
  key: string;
  position?: number; // Position in passage (0-based)
  questionId?: string; // Actual questionId for submission
  clusterQuestionIndex?: number; // clusterQuestionIndex for direct question number calculation
  questionNumber?: number; // Calculated question number (1-based) - CRITICAL: Use this instead of blankId lookup
  correctAnswers: string[];
  caseSensitive?: boolean;
  trimWhitespace?: boolean;
  inputType?: 'text' | 'number' | 'email' | 'url';
  placeholder?: string;
  maxLength?: number;
  choices?: Array<{
    id: string;
    label: string;
    value: string;
    isCorrect?: boolean;
    explanation?: string;
  }>;
  choiceType?: 'dropdown' | 'radio' | 'checkbox';
  points?: number;
  partialCredit?: boolean;
  subject?: string;
  category?: string;
  difficulty?: 'easy' | 'medium' | 'hard';
  tags?: string[];
}

interface EnhancedFillBlankQuestionProps {
  template: string;
  items: FillBlankItem[];
  value: Record<string, string>;
  onChange: (value: Record<string, string>) => void;
  readOnly?: boolean;
  questionIndex: number;
  currentQuestionNumber?: number; // The actual question number (21, 22, 23, 24, 25)
  allAnswers?: Record<string, string>; // All answers from all questions in the part
  blankToQuestionNumberMap?: Record<string, number>; // Mapping from blankId to actual question number
  blankToQuestionIndexMap?: Record<string, number>; // Mapping from blankId to question index (0-based)
  onNavigateToQuestion?: (questionIndex: number) => void; // Callback for navigation
  className?: string;
}

export default function EnhancedFillBlankQuestion({
  template,
  items,
  value,
  onChange,
  readOnly = false,
  questionIndex,
  currentQuestionNumber,
  allAnswers,
  blankToQuestionNumberMap,
  blankToQuestionIndexMap,
  onNavigateToQuestion,
  className,
}: EnhancedFillBlankQuestionProps) {
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Auto-focus the current question's blank when component mounts or currentQuestionNumber changes
  useEffect(() => {
    if (items.length > 0 && !readOnly && currentQuestionNumber) {
      // Find the blank that corresponds to the current question number
      // Use mapping to find blank by actual question number
      let targetBlank = items.find(item => {
        const actualQuestionNumber = blankToQuestionNumberMap?.[item.id];
        return actualQuestionNumber === currentQuestionNumber;
      });

      // Fallback: find by blank number if mapping is not available
      if (!targetBlank) {
        targetBlank = items.find(item => {
          const blankNumber = parseInt(item.id.replace('blank', ''));
          return blankNumber === currentQuestionNumber;
        });
      }

      if (targetBlank) {
        // Use requestAnimationFrame to ensure DOM is ready, then focus
        const focusInput = () => {
          const inputRef = inputRefs.current[targetBlank!.id];
          if (inputRef && !inputRef.disabled) {
            inputRef.focus();
            // Also try to select text if any
            if (inputRef.value) {
              inputRef.select();
            }
          } else {
            // Retry after a short delay if input is not ready
            setTimeout(focusInput, 50);
          }
        };

        // Use requestAnimationFrame for better timing
        requestAnimationFrame(() => {
          setTimeout(focusInput, 100);
        });
      }
    }
  }, [items, readOnly, currentQuestionNumber, blankToQuestionNumberMap]);

  // Parse template and render with interactive blanks
  const renderTemplate = useCallback(() => {
    // First, extract text content from HTML if it contains HTML tags
    let textContent = template;
    if (template.includes('<div') || template.includes('<p>')) {
      // Use regex to extract text content from HTML
      textContent = template
        .replace(/<div[^>]*>/g, '')
        .replace(/<\/div>/g, '')
        .replace(/<p[^>]*>/g, '')
        .replace(/<\/p>/g, '')
        .replace(/<[^>]*>/g, '') // Remove any remaining HTML tags
        .trim();
    }

    // Support both old format (XX) ______ and new format {{blankXX}}
    // Use non-capturing groups for inner digits to avoid extra array elements
    const parts = textContent.split(/(\{\{blank\d+\}\}|\(\d+\)\s*______)/);

    // IMPORTANT: We intentionally ignore the numeric part of blankXX here.
    // The ORDER of placeholders in the passage is what matters and must
    // align with the ORDER of questions (clusterQuestionIndex) in the cluster.
    let placeholderIndex = 0; // 0-based position of blank in passage

    return parts.map((part, index) => {
      // Check for new format {{blankXX}}
      const newFormatMatch = part.match(/\{\{blank(\d+)\}\}/);
      if (newFormatMatch) {
        const position = placeholderIndex;
        placeholderIndex += 1;

        const item = items[position];
        if (!item) {
          return (
            <span key={index} className='text-red-500'>
              [Missing blank at position {position + 1}]
            </span>
          );
        }

        // CRITICAL: Use item.questionNumber (from clusterQuestionIndex) instead of position + 1
        // item.questionNumber is calculated from clusterQuestionIndex + 1, which is the actual question number
        const displayNumber =
          item.questionNumber !== undefined && item.questionNumber !== null
            ? item.questionNumber
            : position + 1; // Fallback to position + 1 if questionNumber is not available
        const isCurrentQuestion = displayNumber === currentQuestionNumber;

        return (
          <span key={index} className='inline-flex items-center mx-1'>
            {renderBlankInput(item, isCurrentQuestion, displayNumber)}
          </span>
        );
      }

      // Check for old format (XX) ______
      const oldFormatMatch = part.match(/\((\d+)\)\s*______/);
      if (oldFormatMatch) {
        const position = placeholderIndex;
        placeholderIndex += 1;

        const item = items[position];
        if (!item) {
          return (
            <span key={index} className='text-red-500'>
              [Missing blank at position {position + 1}]
            </span>
          );
        }

        // CRITICAL: Use item.questionNumber (from clusterQuestionIndex) instead of position + 1
        // item.questionNumber is calculated from clusterQuestionIndex + 1, which is the actual question number
        const displayNumber =
          item.questionNumber !== undefined && item.questionNumber !== null
            ? item.questionNumber
            : position + 1; // Fallback to position + 1 if questionNumber is not available
        const isCurrentQuestion = displayNumber === currentQuestionNumber;

        return (
          <span key={index} className='inline-flex items-center mx-1'>
            {renderBlankInput(item, isCurrentQuestion, displayNumber)}
          </span>
        );
      }

      return <span key={index}>{part}</span>;
    });
  }, [template, items, value, onChange, readOnly, currentQuestionNumber, questionIndex]);

  // Render individual blank input based on configuration
  const renderBlankInput = useCallback(
    (item: FillBlankItem, isCurrentQuestion: boolean = true, blankNumber?: number) => {
      // CRITICAL: Use questionId instead of blankId (item.id) for lookup
      // questionId is the actual question ID, which is unique and reliable
      const key = item.questionId || item.id; // Fallback to item.id for backward compatibility
      // Use allAnswers to show all previous answers, fallback to current value
      const currentValue = allAnswers?.[key] || value[key] || '';

      // Choice-based blanks (dropdown, radio, checkbox)
      if (item.choices && item.choices.length > 0) {
        return renderChoiceInput(item, currentValue, isCurrentQuestion, blankNumber);
      }

      // Text/number input blanks
      return renderTextInput(item, currentValue, isCurrentQuestion, blankNumber);
    },
    [value, allAnswers, onChange, readOnly]
  );

  // Render choice-based input (dropdown, radio, checkbox)
  const renderChoiceInput = useCallback(
    (
      item: FillBlankItem,
      currentValue: string,
      isCurrentQuestion: boolean = true,
      blankNumber?: number
    ) => {
      const handleChoiceChange = (newValue: string) => {
        // CRITICAL: Use questionId instead of blankId (item.id)
        const key = item.questionId || item.id; // Fallback to item.id for backward compatibility
        onChange({
          ...value,
          [key]: newValue,
        });
      };

      if (item.choiceType === 'dropdown') {
        return (
          <Select value={currentValue} onValueChange={handleChoiceChange} disabled={readOnly}>
            <SelectTrigger
              className={cn(
                'w-32 h-8 text-sm',
                'border-border',
                'focus:border-primary focus:ring-2 focus:ring-primary/20',
                'hover:border-primary dark:hover:border-primary',
                readOnly && 'opacity-50 cursor-not-allowed'
              )}
            >
              <SelectValue placeholder={item.placeholder || 'Select...'} />
            </SelectTrigger>
            <SelectContent>
              {item.choices?.map(choice => (
                <SelectItem key={choice.id} value={choice.value}>
                  {choice.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      }

      if (item.choiceType === 'radio') {
        return (
          <RadioGroup
            value={currentValue}
            onValueChange={handleChoiceChange}
            disabled={readOnly}
            className='flex flex-wrap gap-2'
          >
            {item.choices?.map(choice => (
              <div key={choice.id} className='flex items-center space-x-2'>
                <RadioGroupItem
                  value={choice.value}
                  id={`${item.id}-${choice.id}`}
                  className='w-4 h-4'
                />
                <Label htmlFor={`${item.id}-${choice.id}`} className='text-sm cursor-pointer'>
                  {choice.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        );
      }

      if (item.choiceType === 'checkbox') {
        const selectedValues = currentValue ? currentValue.split(',') : [];

        const handleCheckboxChange = (choiceValue: string, checked: boolean) => {
          // CRITICAL: Use questionId instead of blankId (item.id)
          const key = item.questionId || item.id; // Fallback to item.id for backward compatibility
          let newValues: string[];
          if (checked) {
            newValues = [...selectedValues, choiceValue];
          } else {
            newValues = selectedValues.filter(v => v !== choiceValue);
          }

          onChange({
            ...value,
            [key]: newValues.join(','),
          });
        };

        return (
          <div className='flex flex-wrap gap-2'>
            {item.choices?.map(choice => (
              <div key={choice.id} className='flex items-center space-x-2'>
                <Checkbox
                  id={`${item.id}-${choice.id}`}
                  checked={selectedValues.includes(choice.value)}
                  onCheckedChange={checked =>
                    handleCheckboxChange(choice.value, checked as boolean)
                  }
                  disabled={readOnly}
                  className='w-4 h-4'
                />
                <Label htmlFor={`${item.id}-${choice.id}`} className='text-sm cursor-pointer'>
                  {choice.label}
                </Label>
              </div>
            ))}
          </div>
        );
      }

      // Fallback to text input
      return renderTextInput(item, currentValue);
    },
    [value, onChange, readOnly]
  );

  // Render text/number input
  const renderTextInput = useCallback(
    (
      item: FillBlankItem,
      currentValue: string,
      isCurrentQuestion: boolean = true,
      blankNumber?: number
    ) => {
      const handleInputChange = (newValue: string) => {
        // CRITICAL: Use questionId instead of blankId (item.id)
        // questionId is the actual question ID, which is unique and reliable
        const key = item.questionId || item.id; // Fallback to item.id for backward compatibility
        onChange({
          ...value,
          [key]: newValue,
        });
      };

      // CRITICAL: Use questionNumber from item directly, NOT from blankId lookup
      // blankId is not unique and cannot be relied upon for mapping
      // questionNumber is calculated from clusterQuestionIndex + 1 (or position + 1 as fallback)
      let actualQuestionNumber: number | undefined;

      // Priority 1: Use questionNumber from item (calculated from clusterQuestionIndex)
      if (item.questionNumber !== undefined && item.questionNumber !== null) {
        actualQuestionNumber = item.questionNumber;
      }
      // Priority 2: Calculate from clusterQuestionIndex if available
      else if (item.clusterQuestionIndex !== undefined && item.clusterQuestionIndex !== null) {
        actualQuestionNumber = item.clusterQuestionIndex + 1;
      }
      // Priority 3: Use position + 1 as fallback
      else if (item.position !== undefined && item.position !== null) {
        actualQuestionNumber = item.position + 1;
      }
      // Priority 4: Fallback to blankToQuestionNumberMap lookup (for backward compatibility)
      else {
        actualQuestionNumber = blankToQuestionNumberMap?.[item.id];
      }

      const placeholder = actualQuestionNumber
        ? `${actualQuestionNumber}`
        : item.placeholder || 'Enter answer...';

      return (
        <input
          ref={el => {
            inputRefs.current[item.id] = el;
            // Auto-focus when input is rendered and it's the current question
            if (el && isCurrentQuestion && !readOnly) {
              // Use requestAnimationFrame to ensure DOM is ready
              requestAnimationFrame(() => {
                setTimeout(() => {
                  if (el && !el.disabled && document.activeElement !== el) {
                    el.focus();
                    // Select text if any
                    if (el.value) {
                      el.select();
                    }
                  }
                }, 50);
              });
            }
          }}
          type={item.inputType || 'text'}
          value={currentValue}
          onChange={e => handleInputChange(e.target.value)}
          disabled={readOnly || !isCurrentQuestion}
          placeholder={placeholder}
          maxLength={item.maxLength}
          onClick={
            !isCurrentQuestion && onNavigateToQuestion
              ? () => {
                  // Jump to the question for this blank using the mapping
                  const targetQuestionIndex = blankToQuestionIndexMap?.[item.id];
                  if (targetQuestionIndex !== undefined) {
                    onNavigateToQuestion(targetQuestionIndex);
                  }
                }
              : undefined
          }
          className={cn(
            'px-3 py-1 border rounded text-center min-w-[80px] max-w-[120px]',
            isCurrentQuestion
              ? 'border-border bg-card focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary hover:border-primary dark:hover:border-primary'
              : 'border-border bg-gray-100 dark:bg-gray-700 text-muted-foreground cursor-not-allowed',
            'text-sm font-medium',
            readOnly && 'opacity-50 cursor-not-allowed'
          )}
        />
      );
    },
    [
      value,
      onChange,
      readOnly,
      blankToQuestionNumberMap,
      blankToQuestionIndexMap,
      onNavigateToQuestion,
    ]
  );

  return (
    <Card
      className={cn(
        'p-6 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl border border-border shadow-sm',
        className
      )}
    >
      <div className='space-y-4'>
        {/* Template with interactive blanks */}
        <div className='text-base leading-relaxed text-foreground'>{renderTemplate()}</div>

        {/* Show hints if available */}
        {items.some(item => item.choices && item.choices.length > 0) && (
          <div className='text-sm text-muted-foreground bg-primary/10/50 dark:bg-primary/20/20 p-3 rounded-lg border border-primary/50'>
            <span className='font-medium text-primary dark:text-primary'>Hint:</span> Select from
            the available options for each blank.
          </div>
        )}

        {/* Show correct answers for review (if readOnly) */}
        {readOnly && (
          <div className='space-y-2'>
            <div className='text-sm font-medium text-foreground'>Correct Answers:</div>
            {items.map(item => (
              <div key={item.id} className='text-sm text-muted-foreground'>
                <span className='font-medium'>{item.id}:</span> {item.correctAnswers.join(', ')}
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
